import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Baby, Wallet, MapPin, Heart, Sun, Cloud, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';

const Onboarding = () => {
  const navigate = useNavigate();
  const { completeOnboarding, user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    children_ages: [],
    budget_preference: 'mid-range',
    max_distance_km: 25,
    interests: [],
    preferred_days: [],
    indoor_outdoor: 'both'
  });

  const totalSteps = 4;

  const ageGroups = [
    { id: '0-2', label: 'Babies & Toddlers', description: '0-2 years', icon: '👶' },
    { id: '3-5', label: 'Preschool', description: '3-5 years', icon: '🧒' },
    { id: '6-8', label: 'Early Primary', description: '6-8 years', icon: '👦' },
    { id: '9-12', label: 'Tweens', description: '9-12 years', icon: '🧑' },
    { id: '13+', label: 'Teens', description: '13+ years', icon: '👩' }
  ];

  const budgetOptions = [
    { id: 'free', label: 'Free', description: 'Free activities only' },
    { id: 'budget', label: 'Budget', description: 'Under £10 per person' },
    { id: 'mid-range', label: 'Mid-Range', description: '£10-30 per person' },
    { id: 'premium', label: 'Premium', description: '£30+ per person' }
  ];

  const interests = [
    'Playgrounds', 'Museums', 'Farms', 'Soft Play', 'Nature', 
    'Swimming', 'Sports', 'Crafts', 'Theatre', 'Educational'
  ];

  const dayPreferences = [
    { id: 'weekday', label: 'Weekdays' },
    { id: 'weekend', label: 'Weekends' },
    { id: 'holiday', label: 'School Holidays' }
  ];

  const handleToggleAge = (ageId) => {
    setPreferences(prev => ({
      ...prev,
      children_ages: prev.children_ages.includes(ageId)
        ? prev.children_ages.filter(a => a !== ageId)
        : [...prev.children_ages, ageId]
    }));
  };

  const handleToggleInterest = (interest) => {
    setPreferences(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleToggleDay = (dayId) => {
    setPreferences(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(dayId)
        ? prev.preferred_days.filter(d => d !== dayId)
        : [...prev.preferred_days, dayId]
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    const result = await completeOnboarding(preferences);
    if (result.success) {
      navigate('/discover', { replace: true });
    }
    setLoading(false);
  };

  const canProceed = () => {
    if (step === 1) return preferences.children_ages.length > 0;
    if (step === 2) return true;
    if (step === 3) return preferences.interests.length > 0;
    if (step === 4) return true;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col">
      {/* Progress Bar */}
      <div className="px-6 pt-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#666B67]">Step {step} of {totalSteps}</span>
            <button
              onClick={() => navigate('/discover')}
              className="text-sm text-[#666B67] hover:text-[#2C302E]"
            >
              Skip for now
            </button>
          </div>
          <div className="h-2 bg-[#2C302E]/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#E07A5F] rounded-full transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-lg">
          {/* Step 1: Age Groups */}
          {step === 1 && (
            <div className="fade-in-up" style={{ opacity: 1 }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
                  <Baby className="w-8 h-8 text-[#E07A5F]" />
                </div>
                <h1 className="text-2xl font-bold text-[#2C302E] font-['Outfit'] mb-2">
                  What ages are your children?
                </h1>
                <p className="text-[#666B67]">Select all that apply</p>
              </div>

              <div className="grid gap-3">
                {ageGroups.map((age) => (
                  <button
                    key={age.id}
                    onClick={() => handleToggleAge(age.id)}
                    className={`p-4 rounded-2xl border flex items-center gap-4 transition-all ${
                      preferences.children_ages.includes(age.id)
                        ? 'border-[#E07A5F] bg-[#E07A5F]/5'
                        : 'border-[#2C302E]/10 bg-white hover:border-[#2C302E]/20'
                    }`}
                    data-testid={`onboarding-age-${age.id}`}
                  >
                    <span className="text-2xl">{age.icon}</span>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${preferences.children_ages.includes(age.id) ? 'text-[#E07A5F]' : 'text-[#2C302E]'}`}>
                        {age.label}
                      </p>
                      <p className="text-sm text-[#666B67]">{age.description}</p>
                    </div>
                    {preferences.children_ages.includes(age.id) && (
                      <Check className="w-5 h-5 text-[#E07A5F]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Budget & Distance */}
          {step === 2 && (
            <div className="fade-in-up" style={{ opacity: 1 }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#81B29A]/10 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-8 h-8 text-[#81B29A]" />
                </div>
                <h1 className="text-2xl font-bold text-[#2C302E] font-['Outfit'] mb-2">
                  Budget & Travel
                </h1>
                <p className="text-[#666B67]">Help us find activities that fit your lifestyle</p>
              </div>

              <div className="mb-8">
                <h3 className="text-sm font-medium text-[#2C302E] mb-3">Budget preference</h3>
                <div className="grid grid-cols-2 gap-2">
                  {budgetOptions.map((budget) => (
                    <button
                      key={budget.id}
                      onClick={() => setPreferences(prev => ({ ...prev, budget_preference: budget.id }))}
                      className={`p-4 rounded-2xl border text-left transition-all ${
                        preferences.budget_preference === budget.id
                          ? 'border-[#81B29A] bg-[#81B29A]/5'
                          : 'border-[#2C302E]/10 bg-white hover:border-[#2C302E]/20'
                      }`}
                      data-testid={`onboarding-budget-${budget.id}`}
                    >
                      <p className={`font-medium ${preferences.budget_preference === budget.id ? 'text-[#81B29A]' : 'text-[#2C302E]'}`}>
                        {budget.label}
                      </p>
                      <p className="text-xs text-[#666B67]">{budget.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-[#2C302E] mb-3">
                  How far are you willing to travel?
                </h3>
                <div className="bg-white p-4 rounded-2xl border border-[#2C302E]/10">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-[#E07A5F]" />
                    <span className="font-medium text-[#E07A5F]">{preferences.max_distance_km} km</span>
                  </div>
                  <Slider
                    value={[preferences.max_distance_km]}
                    onValueChange={(value) => setPreferences(prev => ({ ...prev, max_distance_km: value[0] }))}
                    max={100}
                    min={5}
                    step={5}
                    data-testid="onboarding-distance-slider"
                  />
                  <div className="flex justify-between text-xs text-[#666B67] mt-2">
                    <span>5 km</span>
                    <span>100 km</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Interests */}
          {step === 3 && (
            <div className="fade-in-up" style={{ opacity: 1 }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-2xl font-bold text-[#2C302E] font-['Outfit'] mb-2">
                  What are you interested in?
                </h1>
                <p className="text-[#666B67]">Select at least one</p>
              </div>

              <div className="flex flex-wrap gap-2 justify-center">
                {interests.map((interest) => (
                  <button
                    key={interest}
                    onClick={() => handleToggleInterest(interest)}
                    className={`px-4 py-2 rounded-full transition-all ${
                      preferences.interests.includes(interest)
                        ? 'bg-[#E07A5F] text-white'
                        : 'bg-white border border-[#2C302E]/10 text-[#666B67] hover:border-[#E07A5F]'
                    }`}
                    data-testid={`onboarding-interest-${interest.toLowerCase().replace(' ', '-')}`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Preferences */}
          {step === 4 && (
            <div className="fade-in-up" style={{ opacity: 1 }}>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
                  <Sun className="w-8 h-8 text-[#E07A5F]" />
                </div>
                <h1 className="text-2xl font-bold text-[#2C302E] font-['Outfit'] mb-2">
                  Final Preferences
                </h1>
                <p className="text-[#666B67]">Almost done!</p>
              </div>

              <div className="mb-8">
                <h3 className="text-sm font-medium text-[#2C302E] mb-3">Preferred days</h3>
                <div className="flex gap-2">
                  {dayPreferences.map((day) => (
                    <button
                      key={day.id}
                      onClick={() => handleToggleDay(day.id)}
                      className={`flex-1 p-3 rounded-2xl border text-center transition-all ${
                        preferences.preferred_days.includes(day.id)
                          ? 'border-[#E07A5F] bg-[#E07A5F]/5 text-[#E07A5F]'
                          : 'border-[#2C302E]/10 bg-white text-[#666B67]'
                      }`}
                      data-testid={`onboarding-day-${day.id}`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-[#2C302E] mb-3">Indoor or outdoor?</h3>
                <div className="grid grid-cols-3 gap-2">
                  {['indoor', 'outdoor', 'both'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setPreferences(prev => ({ ...prev, indoor_outdoor: type }))}
                      className={`p-4 rounded-2xl border flex flex-col items-center gap-2 transition-all ${
                        preferences.indoor_outdoor === type
                          ? 'border-[#81B29A] bg-[#81B29A]/5'
                          : 'border-[#2C302E]/10 bg-white'
                      }`}
                      data-testid={`onboarding-location-${type}`}
                    >
                      {type === 'indoor' && <Cloud className="w-6 h-6 text-[#81B29A]" />}
                      {type === 'outdoor' && <Sun className="w-6 h-6 text-amber-500" />}
                      {type === 'both' && <Heart className="w-6 h-6 text-[#E07A5F]" />}
                      <span className={`text-sm capitalize ${preferences.indoor_outdoor === type ? 'text-[#81B29A]' : 'text-[#666B67]'}`}>
                        {type}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="px-6 py-6 border-t border-[#2C302E]/5">
        <div className="max-w-lg mx-auto flex gap-4">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(s => s - 1)}
              className="flex-1 py-6 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex-1 btn-primary py-6"
              data-testid="onboarding-next-btn"
            >
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading}
              className="flex-1 btn-primary py-6"
              data-testid="onboarding-complete-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Start Exploring
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
